import React, { useState, useEffect } from "react";

import { Button, Checkbox, IconWrapper, TextArea, Typography } from "@king-ict/eupisi-ui/components";
import { ReactComponent as CloseIcon } from "@king-ict/eupisi-ui/components/Icon/CloseBig.svg";
import {
  useDisableBodyScroll,
  enableBodyScroll,
} from "@king-ict/eupisi-ui/hooks";
import { useTranslation } from "react-i18next";

import styles from "./ModalStatus.module.scss";

export interface ModalStatusProps {
  children?: any;
  title?: string;
  id?: string;
  open: boolean;
  minWidth?: boolean;
  showCloseMark?: boolean;
  hideOverlay?: boolean;
  disableBody?: boolean;
  type?: "default" | "confirm" | "message" | "error";
  variant?: "normal" | "medium" | "big";
  confirmText?: string;
  onConfirm?: (description: string, userSelection: number[] | null) => void;
  cancelText?: string;
  onClose: (event: React.MouseEvent<any, MouseEvent>) => void;
  showComment?: boolean,
  userSelection?: string[]
}

export const ModalStatus = ({
  children,
  title = "",
  id,
  open = false,
  minWidth = true,
  hideOverlay = false,
  disableBody = true,
  type = "default",
  variant = "normal",
  showCloseMark = type === "default" ? true : false,
  confirmText,
  onConfirm,
  cancelText,
  onClose,
  showComment = false,
  userSelection
}: ModalStatusProps) => {
  useDisableBodyScroll(disableBody, open);
  const [statusNote, setStatusNote] = useState("");  
  const [confirmEnabled, setConfirmButton] = useState(showComment ? false : true);
  const [userSelectionValue, setUserSelectionValue] = useState(userSelection ? Array(userSelection.length).fill(0) : []);

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm(statusNote, userSelectionValue);
    }
  }

  useEffect(() => {
    //unmount
    return () => {
      enableBodyScroll();
    };
  }, []);

  const onStatusNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    var confirmButtonEnabled = e.target.value ? true : false;
    setStatusNote(e.target.value);
    setConfirmButton(confirmButtonEnabled);
  };

  const onClickUserSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    var elementIndex = parseInt(e.target.value);
    var isChecked: number= e.target.checked ? 1 : 0;
    userSelectionValue[elementIndex] = isChecked;
    setUserSelectionValue(
      userSelectionValue.map((item, index) =>
        elementIndex === index ? item = isChecked : item
      ))

    //setUserSelectionValue(
    //  userSelectionValue.map((item, index) =>
    //    elementIndex === index ? item = isChecked : item
    //  ))
  }

  const { t } = useTranslation();

  const handleGetActions = () => {
    switch (type) {
      case "default":
        return <></>;
      case "confirm":
        return (
          <div className={styles.confirmButtonsContainer}>
            <div>
              <Button color="primary" outline onClick={onClose}>
                {cancelText}
              </Button>
            </div>
            <div>
              <Button color="primary" onClick={handleConfirm} disabled={!confirmEnabled}>
                {confirmText}
              </Button>
            </div>
          </div>
        );
    }
  };

  function getVariant() {
    if (variant) {
      return styles[variant];
    }
  }

  if (open === false) return null;

  var commentTextBox, userSelectionPanel;

  if (showComment) {
    commentTextBox =
      <TextArea
        label={""}
        ariaLabel={t("request.rejectRequestModalNote")}
        value={statusNote}
        maxLength={3600}
        rows={12}
        name={"rejectRequestNote"}
        onChange={onStatusNoteChange}
      />
  }

  if (userSelection && userSelection.length > 0) {
    userSelectionPanel =
      <div>
      {userSelection.map((userSelectionItem, index) => (
        <Checkbox
          label={userSelectionItem}
          value={index.toString()}
          onChange={onClickUserSelection}
          checked={userSelectionValue[index]}
        />
      ))}

      </div>
  }

  return (
    <div
      className={`${styles.parentContainer} ${type === "error" ? styles.error : ""
        }`}
    >
      <div
        className={`${styles.overlay} ${hideOverlay ? styles.off : styles.on}`}
        onClick={onClose}
      ></div>
      <div
        id={id}
        className={`${styles.container} ${type === "error" ? styles.error : ""
          } ${getVariant()} ${minWidth ? styles.minWidth : ""}`}
      >
        <div className={styles.contentWrapper}>
          {title || showCloseMark ? (
            <div className={styles.header}>
              <div className={styles.title}>
                <Typography variant="largeBold">{title}</Typography>
              </div>
              <div className={styles.closeMark} onClick={onClose}>
                {showCloseMark ? (
                  <IconWrapper color="black-color">
                    <CloseIcon aria-label={t("accessibility.closeIcon")} />
                  </IconWrapper>
                ) : (
                  <></>
                )}
              </div>
            </div>
          ) : (
            <></>
          )}
          <div className={styles.content}>{children}</div>
          {commentTextBox}
          {userSelectionPanel}
        </div>
        {handleGetActions()}
      </div>
    </div>
  );
};
